import type { DomainGuid, GridElementTypes } from '..';
import { selectHigherAttentionStatus } from '../derived-state/evaluatedGridElements/selectHigherAttentionStatus';
import type { AttentionStatusType } from './AttentionStatus';

// TODO: should be replaced with 2nd version
export type ForStoringAttentionStatus = {
  toState: () => GridElementPropertyAttentionStatus[];
  delegateMessenger: (
    gridElement: DomainGuid,
    objectType: GridElementTypes
  ) => ForMessaging<AttentionStatusType>;
};

export type ForStoringAttentionStatus2 = {
  toState: () => AttentionStatuses[];
  apply: (
    gridElementId: DomainGuid,
    propertyName: string,
    attentionStatus: AttentionStatusType
  ) => void;
};

// TODO: should be replaced with 2nd version
export class AttentionStatusStorage implements ForStoringAttentionStatus {
  private elementAttentionStatuses: GridElementPropertyAttentionStatus[] = [];

  private constructor(
    elementAttentionStatuses: GridElementPropertyAttentionStatus[]
  ) {
    if (elementAttentionStatuses) {
      this.elementAttentionStatuses = elementAttentionStatuses;
    }
  }

  static create() {
    return new AttentionStatusStorage([]);
  }

  static noStorage() {
    return new NoAttentionStatusStorage();
  }

  private acceptMessage = (element: GridElementPropertyAttentionStatus) => {
    this.elementAttentionStatuses.push(element);
  };

  delegateMessenger(id: DomainGuid, objectType: GridElementTypes) {
    const messenger = new EvaluationMessenger(
      id,
      objectType,
      this.acceptMessage
    );
    return messenger;
  }

  toState() {
    return this.elementAttentionStatuses;
  }
}

// TODO: should be replaced with 2nd version
export class AttentionStatusProvider implements ForProvidingAttentionStatus {
  private elementAttentionStatuses: GridElementPropertyAttentionStatus[] = [];
  elementAttentionStatusesMap: Record<`{${string}}`, AttentionStatusType[]>;

  private constructor(
    elementAttentionStatuses: GridElementPropertyAttentionStatus[]
  ) {
    this.elementAttentionStatuses = elementAttentionStatuses;
    this.elementAttentionStatusesMap = elementAttentionStatuses.reduce<Record<DomainGuid, AttentionStatusType[]>>(
      (map, gridElement) => {
        if (!map[gridElement.gridElementId]) {
          map[gridElement.gridElementId] = [];
        }
        map[gridElement.gridElementId].push(gridElement.attentionStatus);
        return map;
      }
    , {})
  }

  static createFromState(
    elementAttentionStatuses: GridElementPropertyAttentionStatus[]
  ) {
    return new AttentionStatusProvider(elementAttentionStatuses);
  }

  getAllAttentionRequired() {
    return this.elementAttentionStatuses.filter(
      f => f.attentionStatus === 'ATTENTION_REQUIRED'
    );
  }

  getByGridElement(id: DomainGuid) {
    const allPropertiesStatuses = this.elementAttentionStatusesMap[id] || [];

    if (allPropertiesStatuses.length === 0) {
      return 'NOT_EVALUATED';
    }

    return allPropertiesStatuses.reduce((highestStatus, currentStatus) =>
      selectHigherAttentionStatus(highestStatus, currentStatus)
    );
  }
}

export class AttentionStatusStorage2 implements ForStoringAttentionStatus2 {
  private constructor(
    private readonly elementAttentionStatuses: AttentionStatuses[],
    private readonly scenarioId: string
  ) {}

  static create(scenarioId: string) {
    return new AttentionStatusStorage2([], scenarioId);
  }

  static noStorage() {
    return new NoAttentionStatusStorage2();
  }

  apply(
    gridElementId: DomainGuid,
    propertyName: string,
    attentionStatus: AttentionStatusType
  ) {
    this.elementAttentionStatuses.push({
      scenarioId: this.scenarioId,
      gridElementId,
      propertyName,
      attentionStatus
    });
  }

  toState() {
    return this.elementAttentionStatuses;
  }
}

export class AttentionStatusProvider2 implements ForProvidingAttentionStatus2 {
  private elementAttentionStatuses: AttentionStatuses[] = [];

  private constructor(elementAttentionStatuses: AttentionStatuses[]) {
    this.elementAttentionStatuses = elementAttentionStatuses;
  }

  static createFromState(elementAttentionStatuses: AttentionStatuses[]) {
    return new AttentionStatusProvider2(elementAttentionStatuses);
  }

  getAllAttentionRequired() {
    return this.elementAttentionStatuses.filter(
      f => f.attentionStatus === 'ATTENTION_REQUIRED'
    );
  }

  getByGridElement(gridElementId: DomainGuid) {
    const filteredStatuses = this.elementAttentionStatuses.filter(
      evaluation => evaluation.gridElementId === gridElementId
    );

    return this.getHighestStatus(filteredStatuses);
  }

  getByScenario(scenarioId: string) {
    const filteredStatuses = this.elementAttentionStatuses.filter(
      evaluation => evaluation.scenarioId === scenarioId
    );
    return this.getHighestStatus(filteredStatuses);
  }

  getByElementProperty(gridElementId: DomainGuid, propertyName: string) {
    const filteredStatuses = this.elementAttentionStatuses.filter(
      evaluation =>
        evaluation.gridElementId === gridElementId &&
        evaluation.propertyName === propertyName
    );

    return this.getHighestStatus(filteredStatuses);
  }

  private getHighestStatus(attentionStatuses: AttentionStatuses[]) {
    const statuses = attentionStatuses.map(element => element.attentionStatus);

    if (statuses.length === 0) {
      return 'NOT_EVALUATED';
    }

    return statuses.reduce((highestStatus, currentStatus) =>
      selectHigherAttentionStatus(highestStatus, currentStatus)
    );
  }
}

export type ForProvidingAttentionStatus2 = {
  getByGridElement: (gridElementId: DomainGuid) => AttentionStatusType;
  getByScenario: (scenarioId: string) => AttentionStatusType;
  getByElementProperty: (
    gridElementId: DomainGuid,
    propertyName: string
  ) => AttentionStatusType;
};

// TODO: this type should be removed after refactoring
export type ForProvidingAttentionStatus = Omit<
  ForProvidingAttentionStatus2,
  'getByScenario' | 'getByElementProperty'
>;

// TODO: this type should be removed after refactoring
export class NoAttentionStatusStorage implements ForStoringAttentionStatus {
  getByElementId(_id: DomainGuid) {
    return 'NO_ATTENTION_REQUIRED' as AttentionStatusType;
  }

  toState() {
    return [];
  }

  delegateMessenger(_id: DomainGuid, _objectType: GridElementTypes) {
    return Messenger.notDelegated();
  }
}

export class NoAttentionStatusStorage2 implements ForStoringAttentionStatus2 {
  apply() {}

  toState() {
    return [];
  }
}

// TODO: this class should be removed after refactoring
export class EvaluationMessenger implements ForMessaging<AttentionStatusType> {
  private id: DomainGuid;
  private goBackWithMessage: (
    attentionRequired: GridElementPropertyAttentionStatus
  ) => void;

  private gridElementType: GridElementTypes;

  constructor(
    id: DomainGuid,
    objectType: GridElementTypes,
    messageCallback: (element: GridElementPropertyAttentionStatus) => void
  ) {
    this.id = id;
    this.gridElementType = objectType;
    this.goBackWithMessage = messageCallback;
  }

  send(attentionStatus: AttentionStatusType, evaluationName: string) {
    const singlePropertyAttentionStatus: GridElementPropertyAttentionStatus = {
      gridElementId: this.id,
      attentionStatus,
      evaluationName,
      gridElementType: this.gridElementType
    };

    this.goBackWithMessage(singlePropertyAttentionStatus);
  }
}

// TODO: this type should be removed after refactoring
export class NoMessenger<TMessage> implements ForMessaging<TMessage> {
  send() {}
}

// TODO: this type should be removed after refactoring
export class Messenger {
  static notDelegated<TMessage>() {
    return new NoMessenger<TMessage>();
  }
}

// TODO: this type should be removed after refactoring
export type ForMessaging<TMessage> = {
  send: (message: TMessage, description: string) => void;
};

// TODO: remove this type after refactoring and use AttentionStatuses instead
export type GridElementPropertyAttentionStatus = {
  gridElementId: DomainGuid;
  evaluationName: string;
  gridElementType: GridElementTypes;
  attentionStatus: AttentionStatusType;
};

export type GridElementPropertyAttentionStatusByCalculationType = {
  loadFlow: GridElementPropertyAttentionStatus[];
  shortCircuit: AttentionStatuses[];
};

export type GridElementEvaluated = {
  gridElementId: DomainGuid;
  propertyName: string;
  attentionStatus: AttentionStatusType;
};

export type AttentionStatuses = {
  scenarioId: string;
  // gridElementType: GridElementTypes; // TODO: add type to enable filtering by type
  gridElementId: DomainGuid;
  propertyName: string;
  attentionStatus: AttentionStatusType;
};
